import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser, useClerk, useReverification } from '../../lib/auth';
import { getClerkErrorMessage } from '../auth/auth-card.utils';

/** Keep the provider connected until a supported replacement is actually saved. */
export function AccountSignInPanel() {
  const { user: resource } = useUser();
  const user = resource && 'externalAccounts' in resource ? resource : null;
  const { closeUserProfile, signOut } = useClerk();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const updatePassword = useReverification(async () => {
    if (!user) throw new Error('Account details are still loading.');
    if (user.primaryEmailAddress?.verification.status !== 'verified') throw new Error('Verify your primary email address in Profile before adding a password.');
    return user.updatePassword({ newPassword: password });
  });
  const destroy = useReverification(async (id: string) => {
    if (!user) throw new Error('Account details are still loading.');
    await user.reload();
    if (!hasPasswordFallback() && !user.externalAccounts.some(account => account.id !== id && account.verification?.status === 'verified')) {
      throw new Error('Add a password before removing your last connected sign-in method.');
    }
    return user.externalAccounts.find(account => account.id === id)?.destroy();
  });
  const connect = useReverification(async () => {
    if (!user) throw new Error('Account details are still loading.');
    return user.createExternalAccount({ strategy: 'oauth_google', redirectUrl: new URL('/settings', location.origin).href });
  });
  function hasPasswordFallback() {
    return Boolean(user?.passwordEnabled && user.primaryEmailAddress?.verification.status === 'verified');
  }
  async function run(action: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true); setError(null);
    try { await action(); await user?.reload(); }
    catch (failure) { setError(`${getClerkErrorMessage(failure)} Check your sign-in methods before trying again. If verification is unavailable, use password recovery by email.`); }
    finally { setBusy(false); }
  }
  if (!user) return <p role="status">Loading sign-in methods...</p>;
  const email = user.primaryEmailAddress?.emailAddress;
  const emailVerified = user.primaryEmailAddress?.verification.status === 'verified';
  return <section aria-label="Sign-in methods" className="space-y-6">
    <div><h2 className="text-lg font-semibold text-gray-900">Sign-in methods</h2>
      <p className="mt-1 text-sm text-gray-500">Keep a way to sign in before disconnecting an account.</p></div>
    {error && <p role="alert" className="alert-error">{error}</p>}
    {saved && <p role="status" className="text-sm text-gray-700">Password saved. You can now sign in with {email} and your password.</p>}
    <div className="space-y-4">
      {user.externalAccounts.map(account => {
        const canRemove = hasPasswordFallback() || user.externalAccounts.some(other => other.id !== account.id && other.verification?.status === 'verified');
        return <div key={account.id} className="rounded-xl border border-gray-200 p-4 space-y-3">
          <p className="text-sm font-medium text-gray-900">{account.provider.replace(/^oauth_/, '')} · {account.emailAddress}</p>
          {!canRemove && <p className="text-sm text-gray-500">Add a password below before removing this connection.</p>}
          {removeId === account.id ? <div role="group" aria-label="Confirm connection removal" className="space-y-3">
            <p className="text-sm text-gray-700">Use your remaining sign-in method next time. Your projects and issues will stay.</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-secondary" disabled={busy} autoFocus onClick={() => setRemoveId(null)}><span className="text-cap-trim">Cancel</span></button>
              <button type="button" className="btn-danger" disabled={busy} onClick={() => void run(async () => { await destroy(account.id); setRemoveId(null); })}><span className="text-cap-trim">{busy ? 'Removing...' : 'Remove connection'}</span></button>
            </div>
          </div> : <button type="button" className="btn-secondary" disabled={busy || !canRemove} onClick={() => setRemoveId(account.id)}><span className="text-cap-trim">Remove connection</span></button>}
        </div>;
      })}
      {!user.externalAccounts.some(account => account.provider.replace(/^oauth_/, '') === 'google') &&
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => void run(async () => {
          const account = await connect();
          const redirect = account?.verification?.externalVerificationRedirectURL;
          if (redirect) window.location.assign(redirect.href);
        })}><span className="text-cap-trim">Connect Google</span></button>}
    </div>
    {!emailVerified && <p role="status" className="text-sm text-gray-700">Verify your primary email address in Profile before using it as a replacement sign-in method.</p>}
    {!user.passwordEnabled && emailVerified && <form className="space-y-3" onSubmit={event => { event.preventDefault(); void run(async () => {
      await updatePassword(); await user.reload();
      if (!user.passwordEnabled) throw new Error('Password setup was not completed.');
      setPassword(''); setSaved(true);
    }); }}>
      <label htmlFor="account-new-password" className="section-title">Add a password</label>
      <p className="text-sm text-gray-500">Use your verified email, {email}, with this password to sign in.</p>
      <input id="account-new-password" type="password" autoComplete="new-password" className="input w-full" value={password} required disabled={busy} onChange={event => setPassword(event.target.value)} />
      <button type="submit" className="btn-primary" disabled={busy || !password}><span className="text-cap-trim">{busy ? 'Saving...' : 'Save password'}</span></button>
    </form>}
    {hasPasswordFallback() && <p className="text-sm text-gray-700">Email and password sign-in is available for {email}.</p>}
    {error && email && emailVerified && <button type="button" className="btn-secondary" disabled={busy} onClick={() => void run(() => signOut({
      redirectUrl: `/sign-in?recovery=1&email=${encodeURIComponent(email)}&redirect_url=${encodeURIComponent('/settings')}`,
    }))}><span className="text-cap-trim">Sign out and recover by email</span></button>}
    <p className="text-sm text-gray-500">To delete your OverlayQA account, use <Link to="/settings" onClick={() => closeUserProfile()} className="text-primary-600 underline">account settings</Link>.</p>
  </section>;
}
